import apollo from "@/apollo.js"
import gql from "graphql-tag"
import { AuthenticationError } from "../errors"

export const findUser = async (userId) => {
  const response = await apollo.query({
    query: gql(`
      query UserById($userId: Int!) {
        users(id: $userId) {
          id
          displayName
          login
          email
          lastSeen
          authenticationToken
          isAccountAdmin
          allowedToLogin
          mfaEnabled
          avatarUrl
          theme
          teams {
            id
            displayName
            commsEnabled
            metricsEnabled
            chatEnabled
            users {
              id
            }
          }
          preferences {
            dateFormat
            dateTimeFormat
            unifiedSearch
          }
        }
      }
    `),
    variables: { userId: userId },
  })
  return response.data.users[0]
}

export const findCurrentUser = async () => {
  const response = await apollo.query({
    query: gql(`query findCurrentUser {
        currentUser {
          id
          email
          displayName
          authenticationToken
          isAccountAdmin
          isAppAdmin
          mfaEnabled
          theme
          assignedTasks {
            id name description templated
            scheduledType scheduledTime unresolvedFailures
            assignedToTeam {id displayName }
            assignedToUser {id displayName }
            case { id title caseTypeId }
          }
          teams {
            id displayName 
            assignedTasks {
              id name description templated
              scheduledType scheduledTime unresolvedFailures
              assignedToTeam {id displayName }
              assignedToUser {id displayName }
              case { id title caseTypeId }  
            }
          }
          permissions { key }
          bookmarks { id route linkText createdAt }
          screenLayouts { id type label bucketGroupId position teamId }
          preferences {
            dateFormat dateTimeFormat pageSize unifiedSearch
            timeline { conversations created documents participants stateChanges tasks updates viewed notes }
          }
          eventSubscriptions(page: 0, pageSize: 1000) { id case { id title caseType caseTypeId currentState } caseTypeId eventCategory eventType }
          account {
            id
            nickname
            title
            adminConsoleToken
            chatBriefing
            chatReports
            chatMemoryEnabled
            auditEnabled
            timezone
            defaultTeam { id displayName }
            scriptEngineAttrs
            llmApiKeyConfigured
            oidcSso {
              enabled
              issuer
              clientId
              clientSecretConfigured
              buttonLabel
              scopes
              redirectUri
              autoCreateUsers
              defaultTeam
              disablePasswordLogin
            }
            caseTypes {
              id
              name
              templatedFolders {
                id
                caseTypeId
                parentFolderId
                folderName
                folderPath
                createdAt
                updatedAt
              }
              iconSet
              iconCode
              coreTemplate
              bucketGroups {
                name
                buckets { id name } 
              } 
            }
          }
        }
      }
    `),
  })
  return response.data.currentUser
}

export const createUser = async (user) => {
  // Invite flow: omit password so the API creates the user and emails a set-password link.
  try {
    const response = await apollo.mutate({
      mutation: gql(`
            mutation createUserWithPassword($email: String!, $password: String, $displayName: String!) {
              createUserWithPassword(email: $email, password: $password, displayName: $displayName) {
                user { id displayName email login mfaEnabled } error message
              }
            }
          `),
      variables: {
        email: user.email,
        password: null,
        displayName: user.displayName,
      },
    })
    const payload = response && response.data && response.data.createUserWithPassword
    if (!payload) {
      return { error: true, message: "Unexpected response from server." }
    }
    if (payload.error) {
      return { error: true, message: payload.message || "Could not invite user." }
    }
    if (!payload.user) {
      return { error: true, message: payload.message || "Could not invite user." }
    }
    return { user: payload.user, message: payload.message }
  } catch (err) {
    const graphQLError =
      err &&
      err.graphQLErrors &&
      err.graphQLErrors[0]
    const message =
      (graphQLError && graphQLError.message) ||
      (err && err.message) ||
      "Could not invite user."
    return { error: true, message }
  }
}

export const deleteUser = async (user) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation deleteUserById($id: Int!) {
              deleteUser(id:$id) {
                error message
              }
            }
        `),
    variables: { id: user.id },
  })
  return response && response.data && response.data.deleteUser && !response.data.deleteUser.error
}

export const updateUser = async (user) => {
  const response = await apollo.mutate({
    mutation: gql(`
            mutation updateUser($id: Int!, $email: String!, $displayName: String!, $isAccountAdmin: Boolean!, $theme: String, $allowedToLogin: Boolean) {
              updateUser(id: $id, email: $email, displayName: $displayName, isAccountAdmin: $isAccountAdmin, theme: $theme, allowedToLogin: $allowedToLogin) {
                user {id login displayName email isAccountAdmin allowedToLogin mfaEnabled theme} error message
              }
            }
        `),
    variables: {
      id: user.id,
      email: user.email,
      displayName: user.displayName,
      isAccountAdmin: user.isAccountAdmin,
      theme: user.theme,
      allowedToLogin: user.allowedToLogin,
    },
  })
  return response && response.data && response.data.updateUser && !response.data.updateUser.user
}

export const updateUserWithPassword = async (user) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateUserWithPassword($id: Int!, $email: String!, $password: String!, $displayName: String!, $isAccountAdmin: Boolean!, $theme: String, $allowedToLogin: Boolean) {
          updateUserWithPassword(id: $id, email: $email, password: $password, displayName: $displayName, isAccountAdmin: $isAccountAdmin, theme: $theme, allowedToLogin: $allowedToLogin) {
            user {id login displayName email isAccountAdmin allowedToLogin mfaEnabled theme} error message
          }
        }
      `),
    variables: {
      id: user.id,
      email: user.email,
      password: user.password,
      displayName: user.displayName,
      isAccountAdmin: user.isAccountAdmin,
      theme: user.theme,
      allowedToLogin: user.allowedToLogin,
    },
  })
  return response.data
}

export const setupMFA = async (user) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation setupMFA($id: Int!, $totp: String!) {
          setupMFA(id: $id, totp: $totp) {
            error message lowLevelErrorMessage
          }
        }
      `),
    variables: {
      id: user.id,
      totp: user.totp,
    },
  })
  return response.data.setupMFA
}

export const requestForgotPassword = async (email) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation requestPasswordReset($email: String!) {
          requestPasswordReset(email: $email) {
            error message lowLevelErrorMessage
          }
        }
      `),
    variables: { email },
  })
  return response.data.requestPasswordReset
}

export const resetPassword = async (password, token) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation resetPassword($password: String!, $token: String!) {
          resetPassword(password: $password, token: $token) {
            error message lowLevelErrorMessage
          }
        }
      `),
    variables: { password, token },
  })
  return response.data.resetPassword
}

export const disableMFA = async (user) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation disableMFA($id: Int!) {
          disableMFA(id: $id) {
            error message lowLevelErrorMessage
          }
        }
      `),
    variables: {
      id: user.id,
    },
  })
  return response.data.disableMFA
}

export const updateUserPreferences = async (data) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation updateUserPreferences($id: Int!, $userPreferences: UserPreferencesInput) {
          updateUserPreferences(id: $id, userPreferences: $userPreferences) {
            userPreferences {
              dateFormat
              dateTimeFormat
              pageSize
              unifiedSearch
              timeline {conversations created documents participants stateChanges tasks updates viewed notes}
            }
          }
        }
      `),
    variables: { id: data.id, userPreferences: data.userPreferences },
  })
  return response.data
}
export const clearAllNotifications = async () => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation clearAllNotifications {
          clearAllNotifications {
            error message
          }
        }
      `),
  })
  return response.data.clearAllNotifications
}

export const clearNotification = async (notificationId) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation clearNotification($notificationId: Int!) {
          clearNotification(notificationId: $notificationId) {
            error message
          }
        }
      `),
    variables: { notificationId: notificationId },
  })
  return response.data.clearNotification
}

export const searchSavedSearch = async (data) => {
  const response = await apollo.query({
    query: gql(`
    query($savedSearch: SavedSearchInput!, $queryString: String!, $page: Int!, $pageSize: Int!) {
      searchSavedSearch(savedSearch: $savedSearch, queryString: $queryString, page: $page, pageSize: $pageSize) {
        contents {
          total
          messages {
            id
            conversation {
              id
              case { id }
            }
            from { id displayName type email userId teamId }
            archivedBy
            archivedAt
            markedReadAt
            markedReadBy
            sentimentScore
            sentimentMagnitude
            sendStatus
            sendStatusDetail
            sendVerifiedAt
            attachments {
              id
              isInline
            }
            subject
            createdAt
            updatedAt
          }
        }
        lowLevelErrorMessage
        message
        error
      }
    }`),
    variables: { savedSearch: data.savedSearch, queryString: data.queryString, page: data.page, pageSize: data.pageSize },
  })
  return response.data.searchSavedSearch
}

export const reloadSavedSearches = async (pageData) => {
  const listResponse = await apollo.query({
    query: gql(`
        query {
          currentUser {
            account {
              savedSearches {
                id
                name
                query
                systemProvided
                createdAt
                updatedAt
              }
            }
          }
        }`),
    variables: {},
  })

  const account = listResponse.data.currentUser.account
  const savedSearches = (account.savedSearches || []).map((search) => ({ ...search, contents: search.contents || { total: 0, messages: [] } }))
  if (!pageData || !pageData.savedSearchId) {
    return { savedSearches }
  }

  const response = await apollo.query({
    query: gql(`
        query($id: String!, $page: Int!, $pageSize: Int!) {
          currentUser {
            account {
              savedSearches(id: $id) {
                id
                name
                query
                systemProvided
                createdAt
                updatedAt
                contents(page: $page, pageSize: $pageSize) {
                  total
                  messages {
                    id
                    conversation {
                      id
                      case { id }
                    }
                    from { id displayName type email userId teamId }
                    archivedBy
                    archivedAt
                    markedReadAt
                    markedReadBy
                    sentimentScore
                  sentimentMagnitude
                  sendStatus
                  sendStatusDetail
                  sendVerifiedAt
                    attachments {
                      id
                      isInline
                    }
                    subject
                    createdAt
                    updatedAt
                  }
                }
              }
            }
          }
        }`),
    variables: { id: String(pageData.savedSearchId), page: pageData.currentPage, pageSize: pageData.pageSize },
  })
  const selected = response.data.currentUser.account.savedSearches && response.data.currentUser.account.savedSearches[0]
  if (!selected) {
    return { savedSearches }
  }
  const mergedSavedSearches = savedSearches.map((search) => (String(search.id) === String(selected.id) ? selected : search))
  return { savedSearches: mergedSavedSearches }
}

export const reloadSavedSearchesTotals = async () => {
  const response = await apollo.query({
    query: gql(`
        query {
          currentUser {
            account {
              savedSearchTotals {
                id
                name
                total
              }
            }
          }
        }`),
    variables: {},
  })
  const account = response.data.currentUser.account
  return {
    savedSearches: (account.savedSearchTotals || []).map((t) => ({
      id: t.id,
      name: t.name,
      contents: { total: t.total },
    })),
  }
}

export const createSavedSearch = async (savedSearch) => {
  const response = await apollo.mutate({
    mutation: gql(`
        mutation createSavedSearch($name: String!, $query: String!, $pageSize: Int!) {
          createSavedSearch(name: $name, query: $query) {
            savedSearch { 
              id
              name
              query
              systemProvided
              createdAt
              updatedAt
              contents(page: 0, pageSize: $pageSize) {
                total
                messages {
                  id
                  conversation {
                    id
                    case { id }
                  }
                  from { id displayName type email userId teamId }
                  archivedBy
                  archivedAt
                  markedReadAt
                  markedReadBy
                  sentimentScore
                  sentimentMagnitude
                  sendStatus
                  sendStatusDetail
                  sendVerifiedAt
                  attachments {
                    id
                    isInline
                  }
                  subject
                  createdAt
                  updatedAt
                }
              }
             } error message
          }
        }
      `),
    variables: { name: savedSearch.name, query: savedSearch.query, pageSize: savedSearch.pageSize },
  })
  return response.data.createSavedSearch
}

export const deleteSavedSearch = async (savedSearchId) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation deleteSavedSearch($savedSearchId: Int!) {
        deleteSavedSearch(savedSearchId: $savedSearchId) {
          error message
        }
      }
    `),
    variables: { savedSearchId: parseInt(savedSearchId, 10) },
  })
  return response.data.deleteSavedSearch
}

export const renameSavedSearch = async (savedSearchId, newSearchName) => {
  const response = await apollo.mutate({
    mutation: gql(`
      mutation renameSavedSearch($savedSearchId: Int!, $newSearchName: String!) {
        renameSavedSearch(savedSearchId: $savedSearchId, newSearchName: $newSearchName) {
          error message
        }
      }
    `),
    variables: { savedSearchId: parseInt(savedSearchId, 10), newSearchName },
  })
  return response.data.renameSavedSearch
}

export const refreshNotifications = async () => {
  const response = await apollo.query({
    query: gql(`query refreshNotifications {
        currentUser {
          notifications(page: 0, pageSize:1000) { id caseId causedById notificationType eventType title message link linkText severity fileAttachmentId messageId jobId createdAt }
        }
      }`),
  })
  return response.data.currentUser.notifications
}

let notificationSubscriptionObserver = null

export const subscribeToNotifications = (onNotification) => {
  try {
    // Unsubscribe from existing subscription if any
    if (notificationSubscriptionObserver) {
      console.log("subscribeToNotifications", "Unsubscribing from existing subscription")
      notificationSubscriptionObserver.unsubscribe()
      notificationSubscriptionObserver = null
    }
    
    console.log("subscribeToNotifications", "Subscribing to notifications", { apollo: !!apollo, hasSubscribe: apollo && typeof apollo.subscribe === 'function' })
    
    if (!apollo || typeof apollo.subscribe !== 'function') {
      console.error("subscribeToNotifications", "Apollo client not available or subscribe method not found")
      return null
    }
    
    const observable = apollo.subscribe({
      query: gql(`
        subscription notificationCreated {
          notificationCreated {
            notification {
              id
              caseId
              causedById
              notificationType
              eventType
              title
              message
              link
              linkText
              severity
              fileAttachmentId
              messageId
              jobId
              createdAt
            }
          }
        }
      `)
    })
    
    if (!observable) {
      console.error("subscribeToNotifications", "Failed to create subscription observable")
      return null
    }
    
    console.log("subscribeToNotifications", "Subscription observable created, setting up handlers")
    const subscription = observable.subscribe({
      next(response) {
        console.log("subscribeToNotifications", "next handler called", response)
        if (response && response.data && response.data.notificationCreated) {
          const notification = response.data.notificationCreated.notification
          console.log("subscribeToNotifications", "Received notification", notification)
          if (onNotification) {
            onNotification(notification)
          }
        } else {
          console.warn("subscribeToNotifications", "Unexpected response format", response)
        }
      },
      error(err) {
        console.error("subscribeToNotifications", "Subscription error", err)
        // Retry subscription on error after a delay
        window.setTimeout(() => {
          console.log("subscribeToNotifications", "retrying subscribe after error")
          subscribeToNotifications(onNotification)
        }, 2000)
      },
      complete() {
        console.log("subscribeToNotifications", "Subscription completed (connection lost)")
        // Retry subscription when complete (connection lost) after a delay
        window.setTimeout(() => {
          console.log("subscribeToNotifications", "retrying subscribe after completion")
          subscribeToNotifications(onNotification)
        }, 2000)
      },
    })
    
    notificationSubscriptionObserver = subscription
    console.log("subscribeToNotifications", "Subscription set up successfully", { subscription: !!subscription })
    return subscription
  } catch (error) {
    console.error("subscribeToNotifications", "Exception during subscription setup", error)
    // Retry after a delay
    window.setTimeout(() => {
      console.log("subscribeToNotifications", "retrying subscribe after exception")
      subscribeToNotifications(onNotification)
    }, 2000)
    return null
  }
}

export const unsubscribeFromNotifications = () => {
  if (notificationSubscriptionObserver) {
    notificationSubscriptionObserver.unsubscribe()
    notificationSubscriptionObserver = null
    console.log("unsubscribeFromNotifications", "Unsubscribed from notifications")
  } else {
    console.log("unsubscribeFromNotifications", "No active subscription to unsubscribe from")
  }
}

export const reloadBookmarks = async () => {
  const response = await apollo.query({
    query: gql(`query reloadBookmarks {
        currentUser {
          bookmarks { id route linkText createdAt }
        }
      }`),
  })
  return response.data.currentUser.bookmarks
}

export const refreshRecentCases = async () => {
  const response = await apollo.query({
    query: gql(`query refreshRecentCases {
        currentUser {
          account {
            caseTypes {
              id
              name
              iconSet
              iconCode
            }
          }
          recentCases { 
            id
            title
            currentState
            caseTypeId
            caseType
            serviceLevels {
              active
              runningTime
              jeopardizedAt
              breachedAt
              serviceLevelCriteria { id name }
            }
          }
        }
      }`),
  })
  return response.data.currentUser
}

export const reloadAccount = async () => {
  const response = await apollo.query({
    query: gql(`query reloadAccount {
        currentUser {
          account {
            id
            title
            nickname
            adminConsoleToken
            chatBriefing
            chatReports
            chatMemoryEnabled
            auditEnabled
            timezone
            defaultTeam { id displayName }
            scriptEngineAttrs
            llmApiKeyConfigured
            oidcSso {
              enabled
              issuer
              clientId
              clientSecretConfigured
              buttonLabel
              scopes
              redirectUri
              autoCreateUsers
              defaultTeam
              disablePasswordLogin
            }
            serviceLevelCriteria {
              id
              bucketId
              name
              inJeopardyThreshold { days hours minutes taskId }
              inBreachThreshold { days hours minutes taskId }
              enabled
            }
            emailAccounts {
              id
              name
              description
              isDefault
              sendChannel
              receiveChannel
              serverMessage
              hasMessage
              verifySentMailbox
              meta
              imapDetails {
                login
                password
                ssl
                startTLS
                enabled
                host
                port
              }
              smtpDetails {
                login
                password
                ssl
                startTLS
                enabled
                host
                port 
                replyToEmailAddress
                useReplyToAsFrom       
              }
            }
          }
        }
      }
    `),
  })
  return response.data.currentUser
}

export const reloadTeamsAndUsers = async () => {
  const response = await apollo.query({
    query: gql(`query reloadTeamsAndUsers {
        currentUser {
          account {
            users { id email displayName login lastSeen avatarUrl mfaEnabled allowedToLogin confirmedAt inviteExpiresAt }
            teams { 
              id displayName commsEnabled metricsEnabled chatEnabled users { id } 
              assignedTasks {
                id name description templated
                scheduledType scheduledTime unresolvedFailures
                assignedToTeam {id displayName }
                assignedToUser {id displayName }
                case { id title caseTypeId }  
              }          
            }
          }
        }
      }
    `),
  })
  if (response && response.errors) {
    for (const err of response.errors) {
      if (err.message === "You must login to do that") {
        throw new AuthenticationError(err.message)
      }
    }
  }
  return response.data.currentUser
}

export const reloadAssignedTasks = async () => {
  const response = await apollo.query({
    query: gql(`query reloadAssignedTasks {
        currentUser {
          assignedTasks {
            id name description templated
            scheduledType scheduledTime unresolvedFailures
            assignedToTeam {id displayName }
            assignedToUser {id displayName }
            case { id title caseTypeId }
          } 
          account {
            teams { 
              id displayName commsEnabled metricsEnabled users { id } 
              assignedTasks {
                id name description templated
                scheduledType scheduledTime unresolvedFailures
                assignedToTeam {id displayName }
                assignedToUser {id displayName }
                case { id title caseTypeId }  
              }
            }
          }
        }
      }
    `),
  })
  return response.data.currentUser
}

/** Case types core: metadata, bucketGroups structure, emailTemplates, scripts, etc. (no schema, buckets, tasklistTemplates) */
const RELOAD_CASE_TYPES_CORE = gql(`query reloadCaseTypesCore {
  currentUser {
    account {
      caseTypes {
        id name code 
        templatedFolders {
          id caseTypeId parentFolderId folderName folderPath createdAt updatedAt
        }
        description displayName coreTemplate defaultOwnerTeamId uniqueIdentifierField
        iconSet iconCode teams
        emailTemplates {
          id name category template updatedAt templateWrapperId
          emailBodyTemplate defaultEmailBodyTemplate sampleInput
        }
        scripts { 
          id name description createdAt updatedAt
          draftCode publishedCode draftDocument reusable isMicroservice runtimeType runtimeLanguage runtimeImage
        }
        relationships {
          id fromFieldsetKey toFieldsetKey fromRelationKey toRelationKey
          position toId fromId
          fields { id fieldName }
        }
        displayFields { id position fieldset fieldName }
        permissions { 
          id resource { id name resourceType permissionType permissions }
          team { id } permissionFlag 
        }
        resources { id name resourceType permissionType permissions updatedAt }
        statuses { id state position }
        bucketGroups { 
          id name position 
          buckets { id name caseTypeName }
        }
      }
    }
  }
}`)

/** Case types schema only: currentSchema with fieldsets (heavy) */
const RELOAD_CASE_TYPES_SCHEMA = gql(`query reloadCaseTypesSchema {
  currentUser {
    account {
      caseTypes {
        id
        currentSchema {
          name primary_key
          events {
            preCreate postCreate preSave postSave
            preStateChange postStateChange preDelete postDelete
          }
          case_data_views {
            new { rows { columns { fieldset } }}
            edit { rows { columns { fieldset } }}
            detail { rows { columns { fieldset } }}
          }
          fieldsets {
            name display_name display_toggle_field
            fields {
              name type size display_name guidance default_value format
              readonly hidden required display symbol cardinality sequence
              item_type dependent_on formula validation precision validation_message
              options cascading_parent_field time_format input_format input_timezone
              uif relation cascading_options { parent_field_option options }
              lookup_defaults { lookup_field_name type value }
              fields {
                name type size display_name guidance precision default_value format
                readonly hidden required display symbol cardinality item_type
                dependent_on formula time_format input_format input_timezone
                uif relation lookup_defaults { lookup_field_name type value }
                cascading_parent_field cascading_options { parent_field_option options }
                options
              }
              display_toggle_field
            }
          }
        }
      }
    }
  }
}`)

/** Case types tasklist templates only */
const RELOAD_CASE_TYPES_TASKLISTS = gql(`query reloadCaseTypesTasklists {
  currentUser {
    account {
      caseTypes {
        id
        tasklistTemplates {
          id name position createdAt updatedAt
          tasks { 
            id description guidance position actionType actionDefinition key
            createdAt updatedAt parameters {
              id name description parameterType required parameterData createdAt updatedAt
            }
            fieldset
          }
        }
      }
    }
  }
}`)

/** Safely extract caseTypes array from a reloadCaseTypes query response. */
function safeCaseTypes(res) {
  const d = res && res.data
  const u = d && d.currentUser
  const a = u && u.account
  const ct = a && a.caseTypes
  return Array.isArray(ct) ? ct : []
}

/** Build flat buckets list from case types' bucketGroups (Account > CaseType > bucketGroups > buckets). */
function bucketsFromCaseTypes(caseTypes) {
  const list = []
  if (!Array.isArray(caseTypes)) return list
  for (const ct of caseTypes) {
    const cid = ct && ct.id
    const groups = ct && ct.bucketGroups
    if (!Array.isArray(groups)) continue
    for (const bg of groups) {
      const buckets = bg && bg.buckets
      if (!Array.isArray(buckets)) continue
      for (const b of buckets) {
        if (b && b.id != null) {
          list.push({ ...b, caseTypeId: b.caseTypeId != null ? b.caseTypeId : cid })
        }
      }
    }
  }
  return list
}

/** Merge case type arrays by id, combining fields. Attach buckets from flat list grouped by caseTypeId. */
function mergeCaseTypes(arrays, bucketsByCaseTypeId) {
  const byId = {}
  const safeArrays = (arrays || []).filter((arr) => arr && Array.isArray(arr))
  for (const arr of safeArrays) {
    for (const ct of arr) {
      if (ct && ct.id != null) {
        if (!byId[ct.id]) byId[ct.id] = {}
        Object.assign(byId[ct.id], ct)
      }
    }
  }
  const first = safeArrays[0] || []
  const merged = first.map((ct) => (ct && ct.id != null ? byId[ct.id] : null)).filter(Boolean)
  if (bucketsByCaseTypeId) {
    for (const ct of merged) {
      const buckets = bucketsByCaseTypeId[ct.id]
      if (Array.isArray(buckets)) ct.buckets = buckets
    }
  }
  return merged
}

export const reloadCaseTypes = async () => {
  const [coreRes, schemaRes, tasklistsRes] = await Promise.all([
    apollo.query({ query: RELOAD_CASE_TYPES_CORE }),
    apollo.query({ query: RELOAD_CASE_TYPES_SCHEMA }),
    apollo.query({ query: RELOAD_CASE_TYPES_TASKLISTS }),
  ])
  const core = safeCaseTypes(coreRes)
  const schema = safeCaseTypes(schemaRes)
  const tasklists = safeCaseTypes(tasklistsRes)

  const buckets = bucketsFromCaseTypes(core)

  const bucketsByCaseTypeId = {}
  for (const b of buckets) {
    const cid = b.caseTypeId != null ? b.caseTypeId : b.case_type_id
    if (cid != null) {
      if (!bucketsByCaseTypeId[cid]) bucketsByCaseTypeId[cid] = []
      bucketsByCaseTypeId[cid].push(b)
    }
  }
  const merged = mergeCaseTypes([core, schema, tasklists], bucketsByCaseTypeId)
  return { account: { caseTypes: merged } }
}

